import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Form, useNavigation } from 'react-router-dom';
import { toast } from 'react-toastify';
import customFetch from '../../../../utils/customFetch';
import { Switch } from '@headlessui/react';
import QrCode from '../../../produkte/QrCode';
import { useQueryClient } from '@tanstack/react-query';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const NewCardSlideIn = ({ showSlideIn, setShowSlideIn }) => {
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const isSubmitting = navigation.state === 'submitting';

  // State für Bilder
  const [selectedFileFront, setSelectedFileFront] = useState();
  const [previewFront, setPreviewFront] = useState();
  const [selectedFileBack, setSelectedFileBack] = useState();
  const [previewBack, setPreviewBack] = useState();
  const [uploadedImages, setUploadedImages] = useState({
    front: null,
    back: null,
  });

  // State für Formular
  const [formData, setFormData] = useState({
    owner: '',
    status: 'unclaimed',
    name: '',
  });

  // Auswahl Bild oder Farbe
  const [bildOderFarbeFront, setBildOderFarbeFront] = useState('bild');
  const [colorFront, setColorFront] = useState('#D90000');
  const [QRCodeFront, setQRCodeFront] = useState(false);
  const [QRCodeFrontColor, setQRCodeFrontColor] = useState('#FFFFFF');

  const [bildOderFarbeBack, setBildOderFarbeBack] = useState('bild');
  const [colorBack, setColorBack] = useState('#D90000');
  const [QRCodeBack, setQRCodeBack] = useState(false);
  const [QRCodeBackColor, setQRCodeBackColor] = useState('#FFFFFF');

  // Preview für Vorderseite
  useEffect(() => {
    if (!selectedFileFront) {
      setPreviewFront(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFileFront);
    setPreviewFront(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFileFront]);

  // Preview für Rückseite
  useEffect(() => {
    if (!selectedFileBack) {
      setPreviewBack(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFileBack);
    setPreviewBack(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFileBack]);

  const onSelectFileFront = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFileFront(undefined);
      return;
    }
    setSelectedFileFront(e.target.files[0]);
  };

  const onSelectFileBack = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFileBack(undefined);
      return;
    }
    setSelectedFileBack(e.target.files[0]);
  };

  // Bilder hochladen
  const uploadImages = async () => {
    const formData = new FormData();

    if (selectedFileFront) {
      formData.append('graphicFront', selectedFileFront);
    }
    if (selectedFileBack) {
      formData.append('graphicBack', selectedFileBack);
    }

    if (!selectedFileFront && !selectedFileBack) {
      return { front: null, back: null };
    }

    try {
      const response = await customFetch.post('/orders/customize', formData);
      const { data } = response.data;

      let frontUrl = null;
      let backUrl = null;

      // URLs extrahieren
      data.forEach((item) => {
        if (item.graphicFront) frontUrl = item.graphicFront.url;
        if (item.graphicBack) backUrl = item.graphicBack.url;
      });

      setUploadedImages({ front: frontUrl, back: backUrl });
      toast.success('Bilder erfolgreich hochgeladen!');
      return { front: frontUrl, back: backUrl };
    } catch (error) {
      toast.error('Fehler beim Hochladen der Bilder');
      throw error;
    }
  };

  // Karte erstellen
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imageUrls = uploadedImages;

      // Bilder hochladen falls noch nicht geschehen und Bilder ausgewählt
      if (
        (selectedFileFront || selectedFileBack) &&
        (bildOderFarbeFront === 'bild' || bildOderFarbeBack === 'bild')
      ) {
        imageUrls = await uploadImages();
      }

      // Formatiere die Daten für Vorder- und Rückseite
      let graphicFront = '';
      let graphicBack = '';

      if (bildOderFarbeFront === 'farbe') {
        graphicFront = JSON.stringify({
          Farbe: colorFront,
          QR_Code: QRCodeFront,
          QR_Code_Farbe: QRCodeFrontColor,
        });
      } else {
        graphicFront = imageUrls.front || '';
      }

      if (bildOderFarbeBack === 'farbe') {
        graphicBack = JSON.stringify({
          Farbe: colorBack,
          QR_Code: QRCodeBack,
          QR_Code_Farbe: QRCodeBackColor,
        });
      } else {
        graphicBack = imageUrls.back || '';
      }

      // Karte erstellen
      const cardData = {
        owner: formData.owner,
        status: formData.status,
        name: formData.name,
        orderId: formData.orderId,
        graphicFront,
        graphicBack,
      };

      await customFetch.post('/cards', cardData);
      toast.success('Karte erfolgreich erstellt!');
      closeAndReset();
      await queryClient.invalidateQueries({
        queryKey: ['allCards'],
      });
      // Optional: Refresh der Kartenliste triggern
      // window.location.reload();
    } catch (error) {
      toast.error(
        error?.response?.data?.msg || 'Fehler beim Erstellen der Karte'
      );
    }
  };

  // Slide-In schließen und zurücksetzen
  const closeAndReset = () => {
    setShowSlideIn(false);
    setSelectedFileFront(undefined);
    setSelectedFileBack(undefined);
    setPreviewFront(undefined);
    setPreviewBack(undefined);
    setUploadedImages({ front: null, back: null });
    setFormData({
      owner: '',
      status: 'unclaimed',
      name: '',
    });
    setBildOderFarbeFront('bild');
    setBildOderFarbeBack('bild');
    setColorFront('#D90000');
    setColorBack('#D90000');
    setQRCodeFront(false);
    setQRCodeBack(false);
    setQRCodeFrontColor('#FFFFFF');
    setQRCodeBackColor('#FFFFFF');
  };

  return (
    <Transition.Root show={showSlideIn} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeAndReset}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <form
                    onSubmit={handleSubmit}
                    className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
                  >
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                            Neue Karte erstellen
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500"
                              onClick={closeAndReset}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Panel schließen</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        <div className="space-y-6">
                          {/* Kartenname */}
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              Kartenname
                            </label>
                            <div className="mt-2">
                              <input
                                type="text"
                                name="name"
                                id="name"
                                required
                                value={formData.name}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    name: e.target.value,
                                  })
                                }
                                placeholder="z.B. Premium Karte"
                                className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>

                          {/* Besitzer */}
                          <div>
                            <label
                              htmlFor="owner"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              Besitzer (User ID)
                            </label>
                            <div className="mt-2">
                              <input
                                type="text"
                                name="owner"
                                id="owner"
                                value={formData.owner}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    owner: e.target.value,
                                  })
                                }
                                placeholder="User ID eingeben (optional)"
                                className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>

                          {/* Status */}
                          <div>
                            <label
                              htmlFor="status"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              Status
                            </label>
                            <select
                              id="status"
                              name="status"
                              value={formData.status}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  status: e.target.value,
                                })
                              }
                              className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                            >
                              <option value="unclaimed">
                                Nicht beansprucht
                              </option>
                              <option value="active">Aktiv</option>
                              <option value="blocked">Blockiert</option>
                              <option value="demo">Demo</option>
                              <option value="pending">Ausstehend</option>
                            </select>
                          </div>

                          {/* Bestellung verknüpfen */}
                          <div>
                            <label
                              htmlFor="orderId"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              Bestellung verknüpfen (Order ID)
                            </label>
                            <div className="mt-2">
                              <input
                                type="text"
                                name="orderId"
                                id="orderId"
                                value={formData.orderId}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    orderId: e.target.value,
                                  })
                                }
                                placeholder="Bestell-ID eingeben (optional)"
                                className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>

                          {/* Vorderseite */}
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-sm font-medium leading-6 text-gray-900 mb-4">
                              Vorderseite
                            </h3>

                            {/* Auswahl Bild oder Farbe */}
                            <fieldset className="mb-4">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <input
                                    id="bild-front"
                                    name="bildOderFarbeFront"
                                    type="radio"
                                    value="bild"
                                    checked={bildOderFarbeFront === 'bild'}
                                    onChange={() =>
                                      setBildOderFarbeFront('bild')
                                    }
                                    className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                  />
                                  <label
                                    htmlFor="bild-front"
                                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Bild
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    id="farbe-front"
                                    name="bildOderFarbeFront"
                                    type="radio"
                                    value="farbe"
                                    checked={bildOderFarbeFront === 'farbe'}
                                    onChange={() =>
                                      setBildOderFarbeFront('farbe')
                                    }
                                    className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                  />
                                  <label
                                    htmlFor="farbe-front"
                                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Farbe
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            {bildOderFarbeFront === 'farbe' ? (
                              <>
                                {/* Farbe für Vorderseite */}
                                <div className="mb-4">
                                  <label
                                    htmlFor="colorFront"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Farbe auswählen
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      type="color"
                                      id="colorFront"
                                      value={colorFront}
                                      onChange={(e) =>
                                        setColorFront(e.target.value)
                                      }
                                      className="h-10 w-20"
                                    />
                                  </div>
                                </div>

                                {/* Vorschau */}
                                <div className="mb-4">
                                  <div className="aspect-[1.586/1] max-w-sm">
                                    <div
                                      className="h-full w-full rounded-lg flex items-center justify-center"
                                      style={{ backgroundColor: colorFront }}
                                    >
                                      {QRCodeFront && (
                                        <div className="w-1/3">
                                          <QrCode color={QRCodeFrontColor} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* QR Code Option */}
                                <div className="mb-2">
                                  <div className="flex items-center">
                                    <Switch
                                      checked={QRCodeFront}
                                      onChange={setQRCodeFront}
                                      className={classNames(
                                        QRCodeFront
                                          ? 'bg-svisit-primary-600'
                                          : 'bg-gray-200',
                                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-600 focus:ring-offset-2'
                                      )}
                                    >
                                      <span className="sr-only">
                                        QR Code hinzufügen
                                      </span>
                                      <span
                                        aria-hidden="true"
                                        className={classNames(
                                          QRCodeFront
                                            ? 'translate-x-5'
                                            : 'translate-x-0',
                                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                                        )}
                                      />
                                    </Switch>
                                    <span className="ml-3 text-sm font-medium text-gray-900">
                                      QR Code hinzufügen
                                    </span>
                                  </div>
                                  {QRCodeFront && (
                                    <div className="mt-2">
                                      <label
                                        htmlFor="qrColorFront"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                      >
                                        QR Code Farbe
                                      </label>
                                      <input
                                        type="color"
                                        id="qrColorFront"
                                        value={QRCodeFrontColor}
                                        onChange={(e) =>
                                          setQRCodeFrontColor(e.target.value)
                                        }
                                        className="mt-1 h-8 w-16"
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Bild Upload für Vorderseite */}
                                {previewFront && (
                                  <div className="mb-4">
                                    <img
                                      src={previewFront}
                                      alt="Vorschau Vorderseite"
                                      className="h-32 w-auto rounded-lg object-cover"
                                    />
                                  </div>
                                )}
                                <div className="flex text-sm leading-6 text-gray-600">
                                  <label
                                    htmlFor="graphicFront"
                                    className="relative cursor-pointer rounded-md bg-white px-3 py-2 font-semibold text-svisit-primary-600 hover:text-svisit-primary-500 ring-1 ring-inset ring-gray-300"
                                  >
                                    <span>Datei auswählen</span>
                                    <input
                                      id="graphicFront"
                                      name="graphicFront"
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onChange={onSelectFileFront}
                                    />
                                  </label>
                                </div>
                                <p className="text-xs leading-5 text-gray-600 mt-2">
                                  PNG, JPG, SVG bis 5MB
                                </p>
                              </>
                            )}
                          </div>

                          {/* Rückseite */}
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-sm font-medium leading-6 text-gray-900 mb-4">
                              Rückseite
                            </h3>

                            {/* Auswahl Bild oder Farbe */}
                            <fieldset className="mb-4">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <input
                                    id="bild-back"
                                    name="bildOderFarbeBack"
                                    type="radio"
                                    value="bild"
                                    checked={bildOderFarbeBack === 'bild'}
                                    onChange={() =>
                                      setBildOderFarbeBack('bild')
                                    }
                                    className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                  />
                                  <label
                                    htmlFor="bild-back"
                                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Bild
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    id="farbe-back"
                                    name="bildOderFarbeBack"
                                    type="radio"
                                    value="farbe"
                                    checked={bildOderFarbeBack === 'farbe'}
                                    onChange={() =>
                                      setBildOderFarbeBack('farbe')
                                    }
                                    className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                  />
                                  <label
                                    htmlFor="farbe-back"
                                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Farbe
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            {bildOderFarbeBack === 'farbe' ? (
                              <>
                                {/* Farbe für Rückseite */}
                                <div className="mb-4">
                                  <label
                                    htmlFor="colorBack"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Farbe auswählen
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      type="color"
                                      id="colorBack"
                                      value={colorBack}
                                      onChange={(e) =>
                                        setColorBack(e.target.value)
                                      }
                                      className="h-10 w-20"
                                    />
                                  </div>
                                </div>

                                {/* Vorschau */}
                                <div className="mb-4">
                                  <div className="aspect-[1.586/1] max-w-sm">
                                    <div
                                      className="h-full w-full rounded-lg flex items-center justify-center"
                                      style={{ backgroundColor: colorBack }}
                                    >
                                      {QRCodeBack && (
                                        <div className="w-1/3">
                                          <QrCode color={QRCodeBackColor} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* QR Code Option */}
                                <div className="mb-2">
                                  <div className="flex items-center">
                                    <Switch
                                      checked={QRCodeBack}
                                      onChange={setQRCodeBack}
                                      className={classNames(
                                        QRCodeBack
                                          ? 'bg-svisit-primary-600'
                                          : 'bg-gray-200',
                                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-600 focus:ring-offset-2'
                                      )}
                                    >
                                      <span className="sr-only">
                                        QR Code hinzufügen
                                      </span>
                                      <span
                                        aria-hidden="true"
                                        className={classNames(
                                          QRCodeBack
                                            ? 'translate-x-5'
                                            : 'translate-x-0',
                                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                                        )}
                                      />
                                    </Switch>
                                    <span className="ml-3 text-sm font-medium text-gray-900">
                                      QR Code hinzufügen
                                    </span>
                                  </div>
                                  {QRCodeBack && (
                                    <div className="mt-2">
                                      <label
                                        htmlFor="qrColorBack"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                      >
                                        QR Code Farbe
                                      </label>
                                      <input
                                        type="color"
                                        id="qrColorBack"
                                        value={QRCodeBackColor}
                                        onChange={(e) =>
                                          setQRCodeBackColor(e.target.value)
                                        }
                                        className="mt-1 h-8 w-16"
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Bild Upload für Rückseite */}
                                {previewBack && (
                                  <div className="mb-4">
                                    <img
                                      src={previewBack}
                                      alt="Vorschau Rückseite"
                                      className="h-32 w-auto rounded-lg object-cover"
                                    />
                                  </div>
                                )}
                                <div className="flex text-sm leading-6 text-gray-600">
                                  <label
                                    htmlFor="graphicBack"
                                    className="relative cursor-pointer rounded-md bg-white px-3 py-2 font-semibold text-svisit-primary-600 hover:text-svisit-primary-500 ring-1 ring-inset ring-gray-300"
                                  >
                                    <span>Datei auswählen</span>
                                    <input
                                      id="graphicBack"
                                      name="graphicBack"
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onChange={onSelectFileBack}
                                    />
                                  </label>
                                </div>
                                <p className="text-xs leading-5 text-gray-600 mt-2">
                                  PNG, JPG, SVG bis 5MB
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 justify-end px-4 py-4">
                      <button
                        type="button"
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                        onClick={closeAndReset}
                      >
                        Abbrechen
                      </button>
                      <button
                        type="submit"
                        className="ml-4 inline-flex justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        disabled={isSubmitting || !formData.name}
                      >
                        {isSubmitting ? 'Erstellt...' : 'Karte erstellen'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default NewCardSlideIn;
